import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from '../services/firebase';
import posthog from 'posthog-js';

function getAuthErrorMessage(err) {
  const messages = {
    'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Incorrect email or password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your internet connection.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
  };
  return messages[err.code] || err.message;
}

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Register new user
  const register = async (email, password, displayName) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: displayName || email.split('@')[0],
        credits: 10, // Free starting credits
        totalSpent: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscription: {
          plan: 'free',
          status: 'active'
        }
      });

      posthog.identify(user.uid, { email: user.email, name: displayName || email.split('@')[0], plan: 'free' });
      posthog.capture('user_signed_up', { method: 'email' });

      return { success: true, user };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      posthog.captureException(err);
      return { success: false, error: message };
    }
  };

  // Login existing user
  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      posthog.identify(user.uid, { email: user.email });
      posthog.capture('user_logged_in', { method: 'email' });
      return { success: true, user };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      posthog.captureException(err);
      return { success: false, error: message };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      const isNewUser = !userDoc.exists();
      if (isNewUser) {
        // Create user document for first-time Google sign-in
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          credits: 10, // Free starting credits
          totalSpent: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          subscription: {
            plan: 'free',
            status: 'active'
          }
        });
      }

      posthog.identify(user.uid, { email: user.email, name: user.displayName || user.email.split('@')[0] });
      posthog.capture(isNewUser ? 'user_signed_up' : 'user_logged_in', { method: 'google' });

      return { success: true, user };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      posthog.captureException(err);
      return { success: false, error: message };
    }
  };

  // Logout user
  const logout = async () => {
    try {
      setError(null);
      const uid = auth.currentUser?.uid;
      if (uid) {
        posthog.capture('user_logged_out');
      }
      await signOut(auth);
      setUserData(null);
      return { success: true };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      posthog.captureException(err);
      return { success: false, error: message };
    }
  };

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    if (!currentUser) return null;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        return data;
      }
      return null;
    } catch (err) {
      console.error('Error refreshing user data:', err);
      return null;
    }
  };

  // Listen to auth state changes and set up real-time user data listener
  useEffect(() => {
    let unsubscribeUserData = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Clean up previous user data listener
      if (unsubscribeUserData) {
        unsubscribeUserData();
        unsubscribeUserData = null;
      }

      if (user) {
        // Check if user document exists first
        const userDoc = await getDoc(doc(db, 'users', user.uid));

        if (!userDoc.exists()) {
          // User document doesn't exist (shouldn't happen but handle it)
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            displayName: user.email.split('@')[0],
            credits: 10,
            totalSpent: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            subscription: {
              plan: 'free',
              status: 'active'
            }
          });
        }

        // Set up real-time listener for user data (including credits)
        unsubscribeUserData = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
          if (snapshot.exists()) {
            setUserData(snapshot.data());
          }
        });
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserData) {
        unsubscribeUserData();
      }
    };
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    error,
    register,
    login,
    signInWithGoogle,
    logout,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
